import { Badge, Center, Group, Menu } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { NavLink, useNavigate } from 'react-router';
import { NavigationLink } from '../hooks/useNavigationLinks';
import classes from '../UsersMenu.module.css';

interface DesktopMenuProps {
  links: NavigationLink[];
}

export const DesktopMenu = ({ links }: DesktopMenuProps) => {
  const navigate = useNavigate();

  const renderMenuItem = (item: NavigationLink, index: number) => {
    // Handle dividers
    if (item.type === 'divider') {
      return <Menu.Divider key={`divider-${index}`} />;
    }

    // Handle labels
    if (item.type === 'label') {
      return <Menu.Label key={`label-${index}`}>{item.label}</Menu.Label>;
    }

    // Handle nested submenus
    if (item.links) {
      const subMenuItems = item.links.map((subItem, subIndex) =>
        renderMenuItem(subItem, subIndex)
      );

      return (
        <Menu
          key={item.label}
          trigger="hover"
          transitionProps={{ exitDuration: 0 }}
          withinPortal
          position="right-start"
          offset={2}
        >
          <Menu.Target>
            <div>
              <Menu.Item
                rightSection={
                  <IconChevronDown
                    size={14}
                    stroke={1.5}
                    style={{ transform: 'rotate(-90deg)' }}
                  />
                }
              >
                {item.label}
              </Menu.Item>
            </div>
          </Menu.Target>
          <Menu.Dropdown>{subMenuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    // Handle function-based onClick or string-based navigation
    const isFunction = typeof item.link === 'function';

    return (
      <Menu.Item
        key={item.label}
        onClick={() => {
          if (isFunction) {
            (item.link as () => void)();
          } else {
            navigate(item.link as string);
          }
        }}
        color={item.color}
      >
        {item.label}
      </Menu.Item>
    );
  };

  const items = links.map((link) => {
    const menuItems = link.links?.map((item, index) =>
      renderMenuItem(item, index)
    );

    if (menuItems) {
      return (
        <Menu
          key={link.label}
          trigger="hover"
          transitionProps={{ exitDuration: 0 }}
          withinPortal
        >
          <Menu.Target>
            <NavLink to={link.link as string} className={classes.link}>
              {() => (
                <Center>
                  <Group gap={6} wrap="nowrap" align="center">
                    <span className={classes.linkLabel}>{link.label}</span>
                    {link.badge ? (
                      <Badge
                        size="xs"
                        variant="filled"
                        color="blue"
                        radius="sm"
                        tt="uppercase"
                      >
                        {link.badge}
                      </Badge>
                    ) : null}
                    <IconChevronDown size={14} stroke={1.5} />
                  </Group>
                </Center>
              )}
            </NavLink>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <NavLink
        key={link.label}
        to={link.link as string}
        className={classes.link}
      >
        {link.label}
      </NavLink>
    );
  });

  return <>{items}</>;
};
